import type { ScenarioContent } from '../types';

/**
 * Hospitality scenario pages. Copy is grounded in the product specifications
 * published on this site (see src/i18n/locales/en/products.ts) and the company
 * facts on the About page. No competitor claims, no invented case results.
 */
export const hospitalityScenarios: ScenarioContent[] = [
  {
    slug: 'restaurants-cafes',
    nav: 'Restaurants & cafés',
    title: 'Restaurant & Café Patio Heating Guide',
    description:
      'How to heat restaurant and café patios with far-infrared radiant heaters: watts per m², mounting height, wind exposure, model choice and installed examples.',
    h1: 'Restaurant and café patio heating that keeps covers open in winter',
    lede:
      'Radiant heat warms the guest, not the air — which is why it works on a patio that a convection heater cannot hold.',
    answer:
      'For a covered restaurant terrace, plan roughly 80–100 W per m² of seated area; for a terrace open to wind, plan 130–160 W per m². Mount far-infrared heaters 1.8–2.5 m above the floor and aim the radiant face at table height, not at the back wall.',
    sections: [
      {
        heading: 'Why radiant heat suits a restaurant patio',
        paragraphs: [
          'A patio is a leaky room. Every time a door opens, or the wind turns, warm air leaves. Convection heating fights that losing battle by trying to hold an air temperature across an open boundary, which is why gas mushroom heaters burn so much fuel to produce a small ring of comfort.',
          'Far-infrared heaters work differently: the element reaches 300–380 °C, the panel emits radiant energy, and that energy is absorbed by people, tables and the floor rather than the air. Guests feel warmth within seconds of sitting down, and the effect survives light air movement because the heat is delivered directionally.',
          'For an operator, the practical consequences are: no warm-up delay before the first sitting, no stratification loss in a tall space, and a heating cost that scales with occupied tables rather than with the volume of outdoor air.',
        ],
      },
      {
        heading: 'How to size a restaurant terrace',
        bullets: [
          'Measure only the area where guests actually sit. Circulation space and planting do not need coverage.',
          'Add roughly 30% capacity where one or more sides are open to wind, and treat a fully open roof terrace as an outdoor space, not a covered one.',
          'Use the published coverage figures as a starting point: our 1.6 kW model is rated for about 20 m², 2.2 kW for 30 m², 3.2 kW for 40 m² and the 4.0 kW model for 50 m² under sheltered conditions.',
          'Split the load across several smaller units rather than one large unit — coverage is more even, and a single failure does not take out the whole terrace.',
          'Check the electrical supply early: 3.2 kW units draw 13.9 A at 220–240 V, and the 4.0 kW model is a 380 V unit.',
        ],
      },
      {
        heading: 'Mounting, aiming and control',
        paragraphs: [
          'Wall-mounted units at 1.8–2.5 m, tilted slightly downward, give the most predictable result along a façade. Ceiling-mounted units suit pergolas and covered courtyards where a wall is not available. In every case the radiant face should point at the occupied zone; a heater aimed at a wall heats the wall.',
          'Zoning matters more than total output. A 40-seat terrace rarely needs full output at 15:00. Group heaters into two or three circuits with separate switches or thermostats, or use the WiFi-controlled 13W series so staff can switch zones from a phone instead of hunting for a breaker.',
        ],
      },
    ],
    sizing: [
      { space: 'Covered terrace, glazed or sheltered', area: '10 m²', power: '1.0 kW', model: '13A / 13B / 13C 1.0 kW', note: 'Ideal for a two-table balcony or a small smoking corner.' },
      { space: 'Covered terrace, 2–3 sides sheltered', area: '20 m²', power: '1.6–1.8 kW', model: '22B 1.6 kW or 13 series 1.8 kW', note: '22B runs hotter (380 °C) for faster perceived warmth.' },
      { space: 'Semi-open terrace with light breeze', area: '30 m²', power: '2.2–2.4 kW', model: '22B 2.2 kW or 13C 2.4 kW', note: 'Add a windbreak before adding more power.' },
      { space: 'Open frontage or corner site', area: '40 m²', power: '3.2 kW', model: '22B 3.2 kW or 13C 3.2 kW', note: 'Use two units rather than one for even coverage.' },
      { space: 'Large courtyard, engineered layout', area: '50 m²', power: '4.0 kW', model: '13A / 14A 4.0 kW (380 V)', note: 'Three-phase supply required; plan circuits with an electrician.' },
    ],
    sizingNote:
      'Coverage figures are the manufacturer ratings for sheltered installations. Wind, ceiling height above 3 m, and single-glazed or open boundaries reduce effective coverage — treat the table as a starting point and confirm the layout with our team before ordering.',
    recommended: [
      {
        name: '22B Series — 1.6 / 2.2 / 3.2 kW',
        href: '/product/infrared-heaters/22b-series',
        why: '380 °C surface temperature gives the fastest perceived warmth of our wall range, which matters when guests sit down cold and decide within two minutes whether to stay.',
      },
      {
        name: '13C Series — 1.0 to 3.2 kW',
        href: '/product/infrared-heaters/13c-series',
        why: 'The widest panel in the 13 family, so fewer units cover a long façade; IPX4 splash protection and a 300 °C surface suit permanent terrace installation.',
      },
      {
        name: '13W Series — WiFi-controlled',
        href: '/product/infrared-heaters/13w-series',
        why: 'Tuya WiFi, LCD remote and four power levels let front-of-house switch zones as the evening cools instead of running everything from opening.',
      },
    ],
    installs: [
      { image: '/image/cases/case-street-bistro.webp', caption: 'Street-side bistro seating, wall-mounted heaters above the façade line.' },
      { image: '/image/cases/case-restaurant-courtyard.webp', caption: 'Courtyard dining room with ceiling-level radiant coverage.' },
      { image: '/image/cases/case-coffee-bar.webp', caption: 'Coffee bar terrace using 13B units over a narrow frontage.' },
    ],
    specHighlights: [
      { label: 'Output per unit', value: '1.0 – 4.0 kW' },
      { label: 'Surface temperature', value: '300 °C (13 series) / 380 °C (22B)' },
      { label: 'Rated coverage', value: '10 – 50 m² per unit, sheltered' },
      { label: 'Ingress protection', value: 'IPX4 splash protection' },
      { label: 'Mounting height', value: '1.8 – 2.5 m recommended' },
      { label: 'Supply', value: '220–240 V 50/60 Hz; 380 V for 4.0 kW' },
    ],
    faqs: [
      {
        q: 'How many infrared heaters do I need for a 40 m² restaurant patio?',
        a: 'Two 3.2 kW units, or three 2.2 kW units, is the usual starting point for a semi-open 40 m² terrace. Spreading the load across two or three units gives more even coverage than a single larger heater, and keeps a failure from closing the terrace.',
      },
      {
        q: 'Do infrared patio heaters work when it is windy?',
        a: 'Radiant heat is directional, so a light breeze is tolerable, but strong wind physically strips the warm layer from skin and clothing. The correct fix is a windbreak — glass screen, planter wall or awning side — before adding more kilowatts. A heater aimed into moving air will always underperform its rating.',
      },
      {
        q: 'At what height should patio heaters be mounted?',
        a: 'We recommend 1.8–2.5 m above floor level. Below 1.8 m the radiant intensity on a standing guest is uncomfortable; above 2.5 m the footprint widens and the perceived warmth at table height drops, especially with a 300 °C panel.',
      },
      {
        q: 'Can the heaters stay outside in the rain?',
        a: 'Our infrared and portable ranges carry IPX4 splash protection, which covers rain falling on an installed unit. IPX4 is splash protection, not a rating for immersion or for pressure washing, and permanent installations should still be on a circuit with RCD protection.',
      },
      {
        q: 'What electrical supply does a restaurant terrace need?',
        a: 'Each 3.2 kW unit draws 13.9 A at 220–240 V, so a typical four-heater terrace needs a dedicated 3-phase distribution rather than a shared ring main. The 4.0 kW model uses a 380 V supply. Send us the layout and we will return a load schedule you can hand to your electrician.',
      },
    ],
    related: [
      { label: 'Hotel terraces and guest areas', href: '/business/hotels-terraces' },
      { label: 'Rooftop bars and wind-exposed venues', href: '/business/rooftop-bars' },
      { label: 'Sizing calculator', href: '/tools/heater-sizing-calculator' },
      { label: 'Request a terrace layout', href: '/contact' },
    ],
  },
  {
    slug: 'hotels-terraces',
    nav: 'Hotels & terraces',
    title: 'Hotel Terrace & Guest Area Heating',
    description:
      'Infrared heating for hotel terraces, lounges and breakfast courtyards: guest comfort without warm-up, zoning by area, IPX4 installation and specification details.',
    h1: 'Hotel terrace heating that guests use in every season',
    lede:
      'A hotel terrace is a revenue space with a fixed opening calendar. Radiant heat extends the usable season without heating the sky.',
    answer:
      'For hotel terraces, size at 80–100 W per m² of sheltered seating and zone the terrace into two or three control groups so housekeeping can warm the breakfast area without heating the whole courtyard. Wall units at 1.8–2.5 m or ceiling units under a canopy both work; IPX4 is the minimum for exposed installations.',
    sections: [
      {
        heading: 'The operational problem with outdoor guest space',
        paragraphs: [
          'Hotel terraces fail commercially for a simple reason: they are only pleasant for part of the year, but the rent, the furniture and the staff cost are paid all year. Gas patio heaters help, but they need fuel handling, produce combustion moisture, and in a breakfast courtyard the smell travels.',
          'Electric far-infrared changes the operating model. There is no fuel, no open flame, no refuelling round, and no warm-up period: the terrace is comfortable within seconds of switching on, which matches how guests actually use it — a coffee at 07:30, a drink at 19:00, twenty minutes at a time.',
        ],
      },
      {
        heading: 'Specifying for hotel operations',
        bullets: [
          'Zone by space, not by building side: breakfast terrace, lobby terrace and pool bar should be separate circuits so each can run independently.',
          'Prefer wall mounting at 1.8–2.5 m along the façade — it keeps the ceiling line clean and avoids shadowing from parasols.',
          'Under a canopy or pergola, ceiling units free the walls and give even coverage across a dining layout.',
          'Choose a controller staff can use. The 13W series offers Tuya WiFi, an LCD remote and four power levels; simple on/off zoning with a wall switch is often the more reliable choice for seasonal staff.',
          'Ask for the load schedule and the certification pack at quotation stage; hotel engineering teams need both before signing off.',
        ],
      },
      {
        heading: 'What guests notice',
        paragraphs: [
          'Radiant heat is silent and does not move air, so napkins do not lift and candles do not gutter — a small detail that matters in a terrace restaurant. Because the heater warms surfaces rather than air, the effect persists when a door opens, which is the moment convection systems lose their advantage.',
        ],
      },
    ],
    sizing: [
      { space: 'Balcony suite terrace', area: '8–10 m²', power: '1.0 kW', model: '13W 1.0 kW', note: 'Single unit with remote control; guest-adjustable.' },
      { space: 'Lobby terrace seating cluster', area: '20 m²', power: '1.8 kW', model: '13A / 13C 1.8 kW', note: 'Two units facing the seating group.' },
      { space: 'Breakfast courtyard', area: '30–40 m²', power: '2.4–3.2 kW × 2', model: '13C 2.4 kW or 22B 3.2 kW', note: 'Zone with the indoor breakfast room circuit.' },
      { space: 'Pool bar pergola', area: '30 m²', power: '2.2 kW', model: '22B 2.2 kW', note: 'Ceiling or beam mounting under the canopy.' },
      { space: 'Rooftop event terrace', area: '50 m²+', power: '4.0 kW', model: '14A 4.0 kW (380 V)', note: 'Multiple units, windbreak first.' },
    ],
    sizingNote:
      'Hotel terraces are usually semi-open and intermittently occupied, so nameplate coverage overstates real comfort. Treat these figures as a layout starting point and confirm against a floor plan.',
    recommended: [
      {
        name: '13W Series — WiFi and LCD control',
        href: '/product/infrared-heaters/13w-series',
        why: 'Guest-facing terraces need controls that do not require a technician; WiFi plus LCD remote keeps operation inside normal front-of-house routines.',
      },
      {
        name: '22B Series — 380 °C surface',
        href: '/product/infrared-heaters/22b-series',
        why: 'Where a terrace is open on two sides, the higher surface temperature delivers usable warmth at a longer throw than a 300 °C panel of the same wattage.',
      },
      {
        name: 'JHD028-B ceiling heater — 2.0 kW',
        href: '/product/portable-heaters/jhd028-b',
        why: 'A ceiling format for pergolas and covered walkways where wall space is unavailable or visually unacceptable.',
      },
    ],
    installs: [
      { image: '/image/cases/hotel-lounge.webp', caption: 'Hotel lounge terrace with wall-mounted radiant panels.' },
      { image: '/image/cases/evening-terrace.webp', caption: 'Evening terrace service, radiant coverage along the façade.' },
      { image: '/image/cases/covered-lounge.webp', caption: 'Covered lounge seating under a permanent canopy.' },
    ],
    specHighlights: [
      { label: 'Formats', value: 'Wall panel, ceiling, freestanding' },
      { label: 'Output per unit', value: '1.0 – 4.0 kW' },
      { label: 'Control options', value: 'Switch, remote, Tuya WiFi (13W)' },
      { label: 'Ingress protection', value: 'IPX4' },
      { label: 'Certification', value: 'CE, CB, SAA, EMC, RoHS, SASO' },
      { label: 'Mounting height', value: '1.8 – 2.5 m' },
    ],
    faqs: [
      {
        q: 'Can hotel guests control the terrace heaters themselves?',
        a: 'Yes. The 13W series supports Tuya WiFi control from a phone plus an LCD remote, and every range can be put on a simple wall switch or thermostat. For guest rooms and suite terraces we usually recommend a single unit on a local switch so there is nothing to pair or explain at check-in.',
      },
      {
        q: 'How long does a terrace heater take to feel warm?',
        a: 'Radiant warmth is perceptible within seconds because the energy reaches skin and clothing directly rather than heating the air first. There is no boiler or element warm-up cycle to wait for, which is why radiant suits short, repeated use such as breakfast and pre-dinner drinks.',
      },
      {
        q: 'Are the heaters suitable for coastal hotels?',
        a: 'IPX4 covers rain splash, and the housings are powder-coated, but IPX4 is not a salt-air rating. On coastal sites we advise specifying stainless fasteners, rinsing the casing at seasonal intervals, and keeping the unit under a canopy where possible. See the coastal terraces page for the full checklist.',
      },
      {
        q: 'Do you supply certification documents for hotel engineering sign-off?',
        a: 'Yes. CE, CB, SAA, EMC, RoHS, SASO and ISO 9001:2015 documentation is available with the quotation, together with dimensional drawings and a circuit load schedule. Send the destination market and we will confirm which certificates apply.',
      },
      {
        q: 'Can the heaters be integrated with building management?',
        a: 'The 13W series is app- and WiFi-based rather than a native BMS interface. For BMS integration, specify contactor-based zoning on the distribution board: the heaters behave as simple resistive loads, so any BMS with relay outputs can switch them.',
      },
    ],
    related: [
      { label: 'Restaurants and cafés', href: '/business/restaurants-cafes' },
      { label: 'Coastal and covered terraces', href: '/business/covered-terraces-coastal' },
      { label: 'Sizing calculator', href: '/tools/heater-sizing-calculator' },
      { label: 'Talk to our project team', href: '/contact' },
    ],
  },
  {
    slug: 'rooftop-bars',
    nav: 'Rooftop bars',
    title: 'Rooftop Bar Heating: Wind, Height, Output',
    description:
      'Heating a rooftop bar: why wind matters more than wattage, how to use windbreaks, mounting heights for open decks, and radiant output per m² for exposed venues.',
    h1: 'Rooftop bar heating where wind, not temperature, is the real problem',
    lede:
      'On a roof deck the air is moving. Radiant heat is the only electric option that still works — if the layout respects the wind.',
    answer:
      'Rooftop bars should be planned at 130–160 W per m² and always with a windbreak strategy first: glass screens, planter walls or a pergola with side panels. Mount heaters 1.8–2.5 m above the deck, aim them at seating rather than walkways, and expect 20–30% less effective coverage than the same heater on a sheltered terrace.',
    sections: [
      {
        heading: 'Why wattage alone does not solve a rooftop',
        paragraphs: [
          'Every heater has a radiant footprint, and wind erodes it from the edges inward. A 3.2 kW unit that holds 40 m² in still air may hold 25 m² on an exposed roof at 15 km/h. Adding a fourth heater to compensate is expensive and still leaves the upwind tables cold, because the loss is directional.',
          'The sequence that actually works is: reduce air speed at the seating edge, then place heaters on the leeward side of the seating, then add output. Operators who reverse that order usually end up with heaters running at full power and guests still wearing coats.',
        ],
      },
      {
        heading: 'Layout rules for roof decks',
        bullets: [
          'Define a comfort zone of 20–30 m² around each bar or seating cluster instead of trying to heat the whole deck.',
          'Mount on the leeward side, facing across the seating toward the prevailing wind, so guests sit in the radiant path rather than behind a heater.',
          'Keep heaters out of the direct line of open stairwells and lift lobbies, where stack effect creates constant air movement.',
          'Use two circuits: one for the early-evening core seating, one for the late-night overflow area.',
          'Check structural fixing and wind loading with the building engineer before specifying brackets; roof decks expose fixings to far higher loads than terraces.',
        ],
      },
      {
        heading: 'What to specify',
        paragraphs: [
          'For exposed decks we recommend the 22B series: a 380 °C surface temperature at 1.6–3.2 kW gives a longer effective throw than a 300 °C panel of similar wattage. Wall mounting at 1.8–2.5 m along a parapet, angled down toward seating, is the most predictable arrangement. Where no wall exists, freestanding units with weighted bases are the practical alternative, accepting that they can be moved by staff as the wind shifts.',
          'The 22B is IPX4 rated and available for 220–240 V supply; the 3.2 kW unit draws 13.9 A, so plan a dedicated circuit per pair of heaters rather than daisy-chaining.',
        ],
      },
    ],
    sizing: [
      { space: 'Sheltered roof terrace behind a parapet', area: '20 m²', power: '1.6–1.8 kW', model: '22B 1.6 kW', note: 'Parapet acts as a partial windbreak.' },
      { space: 'Semi-exposed deck with glass screens', area: '30 m²', power: '2.2 kW', model: '22B 2.2 kW', note: 'Screens on the two windward sides.' },
      { space: 'Open corner deck', area: '30–40 m²', power: '3.2 kW', model: '22B 3.2 kW', note: 'Two units, positioned downwind of seating.' },
      { space: 'Bar counter under pergola', area: '15 m²', power: '1.6 kW × 2', model: '22B 1.6 kW or JHD028-B ceiling', note: 'Overhead mounting keeps the counter clear.' },
      { space: 'Large roof event space', area: '50 m²+', power: '4.0 kW', model: '14A 4.0 kW (380 V)', note: 'Engineered layout; windbreaks mandatory.' },
    ],
    sizingNote:
      'Exposed decks lose 20–30% of rated coverage to wind. If the venue cannot add screens or a pergola, plan for the higher end of the watt-per-m² range and accept a smaller comfort zone rather than a larger lukewarm one.',
    recommended: [
      {
        name: '22B Series — highest surface temperature',
        href: '/product/infrared-heaters/22b-series',
        why: '380 °C at 1.6–3.2 kW produces the longest usable throw in our wall-mounted range, which is what an exposed deck needs most.',
      },
      {
        name: '13C Series — wide panel, 3.2 kW',
        href: '/product/infrared-heaters/13c-series',
        why: 'A wider radiant face covers a long bar counter with fewer fixing points where wall space is limited.',
      },
      {
        name: 'JHL004-B freestanding patio heater — 3.0 kW',
        href: '/product/portable-heaters/jhl004-b',
        why: 'When no wall or beam is available, a freestanding 3.0 kW unit can be repositioned as the wind shifts between seasons.',
      },
    ],
    installs: [
      { image: '/image/cases/case-bar-front.webp', caption: 'Bar frontage with 22B units mounted above the counter line.' },
      { image: '/image/cases/evening-terrace.webp', caption: 'Evening service on a semi-exposed terrace.' },
      { image: '/image/cases/ocean-patio.webp', caption: 'Exposed waterfront seating, screens plus radiant heat.' },
    ],
    specHighlights: [
      { label: 'Surface temperature', value: '380 °C (22B)' },
      { label: 'Output per unit', value: '1.6 / 2.2 / 3.2 kW' },
      { label: 'Rated coverage', value: '20 / 30 / 40 m² sheltered' },
      { label: 'Mounting height', value: '1.8 – 2.5 m' },
      { label: 'Ingress protection', value: 'IPX4' },
      { label: 'Current draw', value: '7 / 9.6 / 13.9 A at 220–240 V' },
    ],
    faqs: [
      {
        q: 'How do I heat a rooftop bar that is always windy?',
        a: 'Reduce air speed at the seating edge first — glass screens, planter walls, or a pergola with side panels. Then mount heaters on the leeward side facing the seating. Only then add output. On an exposed roof, adding heaters without a windbreak typically raises running cost without fixing the cold tables.',
      },
      {
        q: 'How much coverage does a heater lose in wind?',
        a: 'Assume 20–30% loss on a semi-exposed deck and more at gusty corners. That is why we quote rooftop coverage separately from the sheltered rating: a 3.2 kW unit rated at 40 m² sheltered is realistically a 28–32 m² heater on an open roof.',
      },
      {
        q: 'Can heaters be mounted on a parapet wall?',
        a: 'Yes, provided the fixing is engineered for wind load and the heater faces inward over the seating. Parapet mounting keeps the radiant path above head height and clear of furniture, and the parapet itself becomes part of the windbreak.',
      },
      {
        q: 'Are freestanding heaters or wall heaters better on a roof?',
        a: 'Wall or beam mounting is more predictable and harder to knock over. Freestanding units win where there is no structure to fix to, or where the seating layout changes seasonally — at the cost of floor space and stability in gusts.',
      },
      {
        q: 'What power supply does a rooftop installation need?',
        a: 'Each 22B 3.2 kW unit draws 13.9 A at 220–240 V. For a deck with six units, plan a three-phase distribution with per-zone RCD protection; rooftop circuits also need weatherproof enclosures and cable routing rated for UV exposure.',
      },
    ],
    related: [
      { label: 'Restaurants and cafés', href: '/business/restaurants-cafes' },
      { label: 'Coastal and covered terraces', href: '/business/covered-terraces-coastal' },
      { label: 'Sizing calculator', href: '/tools/heater-sizing-calculator' },
      { label: 'Send us your deck plan', href: '/contact' },
    ],
  },
];
